import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import IntegrationHero from '../../components/integration-hero'
import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import { getCanonicalUrl } from '../../utils/canonical'
import DarkNav from '../../components/dark-nav'
import './microsoft-teams.css'

const MicrosoftTeamsIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    // Scroll to top functionality
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const trackEvent = (eventName, properties = {}) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Microsoft Teams' }
  ]

  const heroContent = {
    title: "DataFlows SMS for Microsoft Teams",
    subtitle: "Send & receive SMS inside your Teams channels",
    description: "Turn Microsoft Teams into a shared, two-way SMS inbox for your whole team. Assign virtual numbers to channels, reply from Teams, and track conversations with powerful dashboards and reports.",
    primaryCta: {
      text: "Install DataFlows for Teams",
      href: "https://appsource.microsoft.com/en-us/product/office/WA200009078?tab=Overview",
      onClick: () => trackEvent('teams_install_clicked')
    },
    secondaryCta: {
      text: "Setup Guide",
      href: "/teams-setup-guide.html"
    }
  }

  const features = [
    {
      icon: "📞",
      title: "Channel-level Virtual Numbers",
      description: "Assign dedicated virtual numbers to each Teams channel. This makes every channel a shared inbox where all messages are visible to the team. Numbers can be local or international, and administrators can manage which channels accept inbound messages."
    },
    {
      icon: "💬",
      title: "Two-way Messaging & Threading",
      description: "Inbound messages are stored as threaded conversations in Teams; replies preserve continuity and maintain a single conversation per contact. Messages support media attachments where permitted and retain full metadata for auditing."
    },
    {
      icon: "📊",
      title: "Advanced Dashboard & Reporting",
      description: "Visualize message traffic with heatmaps, direction indicators (inbound/outbound), and per-channel stats. Filter by date range, export CSVs for compliance, and view cost summaries so finance teams can reconcile SMS spend accurately."
    },
    {
      icon: "🏷️",
      title: "Sender ID & Brand Control",
      description: "Configure Sender IDs per channel (where regulations allow) to present a consistent brand identity. Validate numbers and track segmentation to ensure messages appear correctly across carriers."
    }
  ]

  const benefits = [
    "Centralised customer communication inside the collaboration tool your teams already use",
    "Faster response times — reply in channel instead of switching apps",
    "Shared ownership: any available agent can reply, improving SLAs and reducing missed messages",
    "Auditability: conversation history is preserved, and exports support compliance reviews"
  ]

  const quickStartSteps = [
    {
      step: 1,
      title: "Sign up for DataFlows",
      description: "Create your DataFlows SMS account with instant activation",
      cta: "Sign Up Now"
    },
    {
      step: 2,
      title: "Install Teams App",
      description: "Install the DataFlows Teams app from your Microsoft tenant",
      cta: "Install App"
    },
    {
      step: 3,
      title: "Assign Virtual Numbers",
      description: "Admin: assign virtual numbers to channels under Settings → Channels"
    },
    {
      step: 4,
      title: "Start Messaging",
      description: "Begin receiving and sending SMS messages immediately"
    }
  ]

  const faqItems = [
    {
      question: "How do I assign a number to a Teams channel?",
      answer: "In your DataFlows dashboard, navigate to Settings → Channels. Select the channel and assign an available virtual number. The channel will immediately start receiving inbound messages for that number."
    },
    {
      question: "Can multiple channels share a number?",
      answer: "No, each virtual number can only be assigned to one channel at a time. This ensures message routing clarity and prevents confusion in team communications."
    },
    {
      question: "Are inbound messages stored in Azure?",
      answer: "Messages are securely stored in DataFlows' infrastructure with end-to-end encryption. We provide configurable retention policies and can integrate with your Azure compliance requirements for enterprise customers."
    },
    {
      question: "What happens if a team member leaves?",
      answer: "Channel-based messaging ensures continuity. When team members leave, their message history remains in the channel for context, and remaining team members can continue conversations seamlessly."
    }
  ]

  return (
    <div className="teams-integration-container">
      <DarkNav />
      <Helmet>
        <title>DataFlows SMS for Microsoft Teams — Send & Receive SMS in Teams</title>
        <meta
          name="description"
          content="Integrate DataFlows SMS with Microsoft Teams to send and receive SMS inside channels. Shared inboxes, virtual numbers, reporting, and fast onboarding."
        />
        <meta name="keywords" content="Microsoft Teams SMS, Teams integration, business SMS, shared inbox, virtual numbers, team messaging" />
        <meta property="og:title" content="DataFlows SMS for Microsoft Teams Integration" />
        <meta property="og:description" content="Turn Microsoft Teams into a shared SMS inbox. Assign virtual numbers to channels and manage customer conversations as a team." />
        <meta property="og:image" content="https://dataflows.com.au/images/integrations/teams/Dashboard.png" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/microsoft-teams')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Microsoft Teams", "item": "https://dataflows.com.au/integrations/microsoft-teams" }
          ]
        })}</script>
      </Helmet>

      {/* Header will be inherited from main layout */}
      
      {/* Breadcrumbs */}
      <div className="teams-integration-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <IntegrationHero {...heroContent} />

      {/* Dashboard Preview */}
      <section className="teams-dashboard-preview" data-aos="fade-up">
        <div className="container">
          <img
            src="/images/integrations/teams/Dashboard.png"
            alt="Complete SMS tracking dashboard showing Microsoft Teams channel integration with message threads and analytics"
            loading="lazy"
            className="dashboard-screenshot"
          />
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="teams-video-demo" data-aos="fade-up">
        <div className="container">
          <div className="video-demo-content">
            <h2 className="section-title">See DataFlows Teams Integration in Action</h2>
            <p className="section-description">
              Watch how seamlessly SMS messages integrate with your Teams workflow, from receiving customer messages to sending replies and tracking analytics.
            </p>
            <div className="video-wrapper">
              <iframe 
                width="560" 
                height="315" 
                src="https://www.youtube-nocookie.com/embed/HvghS2WY5-I?si=x1l_fKI9dQQ7OsIw&controls=0" 
                title="DataFlows Microsoft Teams Integration Demo" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="teams-features" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Powerful Features for Team Collaboration</h2>
            <p className="section-description">
              Everything you need to manage SMS communications effectively within Microsoft Teams
            </p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div 
                  className="feature-icon emoji-icon"
                  style={{
                    fontSize: '48px',
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="teams-screenshots">
        <div className="container">
          <h2 className="section-title" data-aos="fade-up">How It Works in Practice</h2>
          
          <div className="screenshot-grid">
            <div className="screenshot-item" data-aos="fade-right">
              <img
                src="/images/integrations/teams/Channel Inbound Receive.png"
                alt="Receiving SMS messages in Microsoft Teams channel showing inbound message notifications"
                loading="lazy"
                className="screenshot-image"
              />
              <div className="screenshot-content">
                <h3>Receive Customer Messages</h3>
                <p>Inbound SMS messages appear directly in your Teams channel with full sender details and message content. Team members get instant notifications and can view the complete conversation history.</p>
              </div>
            </div>

            <div className="screenshot-item reverse" data-aos="fade-left">
              <img
                src="/images/integrations/teams/Channel Reply outbound confirmation.png"
                alt="SMS reply confirmation in Microsoft Teams showing successful message delivery"
                loading="lazy"
                className="screenshot-image"
              />
              <div className="screenshot-content">
                <h3>Send Replies with Confirmation</h3>
                <p>Reply to SMS messages directly from Teams with instant delivery confirmation. Track message status, delivery time, and costs all within the Teams interface.</p>
              </div>
            </div>

            <div className="screenshot-item" data-aos="fade-right">
              <img
                src="/images/integrations/teams/Compose SMS from channel tab.png"
                alt="Composing SMS messages from Microsoft Teams channel tab with recipient and sender ID selection"
                loading="lazy"
                className="screenshot-image"
              />
              <div className="screenshot-content">
                <h3>Compose New Messages</h3>
                <p>Send new SMS messages to customers directly from Teams. Select your sender ID, choose recipients, and track character count with real-time cost estimation.</p>
              </div>
            </div>

            <div className="screenshot-item reverse" data-aos="fade-left">
              <img
                src="/images/integrations/teams/analytics.png"
                alt="SMS analytics dashboard showing message statistics, delivery reports and CSV export options"
                loading="lazy"
                className="screenshot-image"
              />
              <div className="screenshot-content">
                <h3>Analytics & Reporting</h3>
                <p>Access comprehensive SMS analytics with message statistics, delivery reports, and cost tracking. Export data to CSV for compliance and financial reporting.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Experience */}
      <section className="teams-mobile" data-aos="fade-up">
        <div className="container">
          <div className="mobile-content">
            <div className="mobile-text" data-aos="fade-right">
              <h2 className="section-title">Seamless Mobile Experience</h2>
              <p className="section-description">
                Take your SMS conversations on the go with Microsoft Teams mobile. Reply to customer messages, send new SMS, and track delivery status from anywhere.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={index} className="benefit-item">
                    <svg className="benefit-check" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="currentColor"/>
                    </svg>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mobile-image" data-aos="fade-left">
              <img
                src="/images/integrations/teams/Mobile View.png"
                alt="Microsoft Teams mobile app showing DataFlows SMS integration with message notifications and reply interface"
                loading="lazy"
                className="mobile-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Section */}
      <section className="teams-quick-start" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get Started in 4 Simple Steps</h2>
            <p className="section-description">
              Set up DataFlows SMS for Microsoft Teams in minutes, not hours
            </p>
          </div>
          
          <div className="steps-grid">
            {quickStartSteps.map((step, index) => (
              <div key={step.step} className="step-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="step-number">{step.step}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {step.cta && (
                  <a
                    href="https://sms.dataflows.com.au/register"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="step-cta"
                    onClick={() => trackEvent('signup_cta_clicked', { source: 'teams-quick-start' })}
                  >
                    {step.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Summary */}
      <section className="teams-pricing" data-aos="fade-up">
        <div className="container">
          <div className="pricing-content">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <div className="pricing-grid">
              <div className="pricing-item">
                <div className="price">3.5¢ AUD</div>
                <div className="price-label">Per Outbound SMS</div>
              </div>
              <div className="pricing-item">
                <div className="price">Free</div>
                <div className="price-label">Inbound SMS</div>
              </div>
              <div className="pricing-item">
                <div className="price">No Contracts</div>
                <div className="price-label">Prepaid Credits System</div>
              </div>
            </div>
            <div className="pricing-cta">
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('signup_cta_clicked', { source: 'teams-pricing' })}
              >
                Start Free Trial
              </a>
              <a
                href="/contact-us"
                className="cta-button secondary"
                onClick={() => trackEvent('contact_support_clicked', { integration: 'microsoft-teams' })}
              >
                Enterprise Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="teams-faq" data-aos="fade-up">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item" data-aos="fade-up" data-aos-delay={index * 100}>
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="teams-final-cta">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <h2 className="cta-title">Ready to Transform Your Team Communication?</h2>
            <p className="cta-description">
              Join thousands of teams using DataFlows SMS for Microsoft Teams to streamline customer communications
            </p>
            <div className="cta-buttons">
              <a
                href="https://appsource.microsoft.com/en-us/product/office/WA200009078?tab=Overview"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('teams_install_clicked', { source: 'final-cta' })}
              >
                Install for Teams
              </a>
              <a
                href="/contact-us"
                className="cta-button secondary"
                onClick={() => trackEvent('contact_support_clicked', { source: 'final-cta' })}
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer will be inherited from main layout */}
      <Footer />

      {/* Scroll to Top Button */}
      <div 
        className={`scroll-to-top ${showScrollTop ? 'show' : ''}`} 
        onClick={scrollToTop}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/>
        </svg>
      </div>
    </div>
  )
}

export default MicrosoftTeamsIntegration